import { CSSProperties, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ElementRect, nullRect, useElementRect } from "../../../hooks/useElementRect";
import { useComponentLoadUnload } from "../../../services/helpers";
import { Box } from "@mui/material";
import React from "react";
import FlexChild from "./FlexChild";

interface RectUpdateContextType {
    registerUpdate: (id: string, updateFunc: () => void) => void;
    forceUpdate: () => void;
    containerRect: ElementRect;
    childRects:Box[]
}
  
const defaultContextValue: RectUpdateContextType = {
    registerUpdate: () => {},
    forceUpdate: () => {},
    containerRect: {top:0,left:0,bottom:0,right:0,x:0,y:0,height:0,width:0},
    childRects: []
};
  
export const RectUpdateContext = createContext<RectUpdateContextType>(defaultContextValue);
  
interface Box extends ElementRect {
  id: number;
}
interface UpdateFunctions {
    [key: string]: () => void;
  }
interface FlexContainerProps {
    containerRect?: ElementRect;
    children: React.ReactNode;
    flexWrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
    alignItems?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
    justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
    gap?: number;
    style?: CSSProperties; // Add the style property
}
const FlexContainer: React.FC<FlexContainerProps> = ({
    containerRect=nullRect,
    children,
    flexWrap = 'wrap',
    alignItems = 'center',
    justifyContent = 'flex-start',
    gap = 1,
    style,
  }) => {
    //   useComponentLoadUnload(
    //     'FlexContainer',
    //     () => {    },
    //     () => {    }
    // );  
    const [childRects, setChildRects] = useState<Box[]>([]);
    const [updateFunctions, setUpdateFunctions] = useState<UpdateFunctions>({});
    
    const handleReport = useCallback((id: number) => (rect: ElementRect) => {
        setChildRects(prev => {
          const index = prev.findIndex(box => box.id === id);
          if (index !== -1) {
            // Replace the existing entry
            const newBoxes = [...prev];
            newBoxes[index] = { ...rect, id };
            return newBoxes;
          } else {
            // Add a new entry
            return [...prev, { ...rect, id }];
          }
        });
      }, []);
    let nextId = -1;
    const usedIds = new Set<number>();      
    // React.Children.map allows for mapping over children and injecting props
    const enhancedChildren = React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.type === FlexChild) { 
        let id:number;
    
        if (child.props.order !== undefined) {
          const order = Number(child.props.order);
          if (usedIds.has(order)) {
            id = nextId--;
          } else {
            id = order;
            usedIds.add(order);
          }
        } else {
          id = nextId--;
        }
    
        // Capture the existing onReport function if it exists
        const existingOnReport = child.props.onReport;
    
        const newOnReport = (rect:ElementRect) => {
          // If there's an existing onReport function, call it with the rect
          if (typeof existingOnReport === 'function') {
            existingOnReport(rect);
          }
    
          // Then call the handleReport logic with the rect
          const curriedHandleReport = handleReport(id);
          curriedHandleReport(rect);
        };
    
        return React.cloneElement(child, {
          ...child.props,
          onReport: newOnReport,
        });
      }
    
      // Return the child unmodified if it's not a FlexChild or doesn't meet the conditions
      return child;
    });
    
      
    // Register children's update functions
    const registerUpdate = useCallback((id: string, updateFunc: (() => void) | null) => {
        setUpdateFunctions((prev) => {
            // If updateFunc is null, remove the entry to unregister the update function
            if (updateFunc === null) {
                const { [id]: _, ...newState } = prev;
                return newState;
            }
            // If updateFunc is not a function, log a warning and leave the state unchanged
            else if (typeof updateFunc !== 'function') {
                console.warn(`Attempted to register a non-function update for id: ${id}`);
                return prev;
            }
            // Otherwise, update or add the new update function
            else {
                return { ...prev, [id]: updateFunc };
            }
        });
    }, []);
    
  
    const forceUpdate = useCallback(() => {
      Object.entries(updateFunctions).forEach(([id, updateFunc]) => {
          if (typeof updateFunc === 'function') {
            updateFunc();
          } else {
            console.warn(`Registered update function for id: ${id} is not a function.`);
          }
      });
    }, [updateFunctions]);
    
    useEffect(() => {
        // Set a timeout to call forceUpdate after 1 second
        // console.log('force1')
        const timer = setTimeout(() => {
          forceUpdate();
        }, 300);
      
        // Cleanup function to clear the timeout if the component unmounts
        return () => clearTimeout(timer);
      }, [forceUpdate]); 
    //   const prevRectRef = useRef<ElementRect>({ height: null, width: null });
      const prevRectRef = useRef<ElementRect>(nullRect);
      useEffect(() => {
        const { height, width } = containerRect;
        const { height: prevHeight, width: prevWidth } = prevRectRef.current;
    
        if (height !== prevHeight || width !== prevWidth) {
            forceUpdate(); // Call forceUpdate
            // Update prevRectRef with the new values
            prevRectRef.current = { ...prevRectRef.current, height, width };
        }
    }, [containerRect, forceUpdate]);
    return (
      <RectUpdateContext.Provider value={{ registerUpdate, forceUpdate, containerRect, childRects }}>
        <Box
            sx={{
                display: 'flex',
                flexWrap,
                alignItems,
                justifyContent,
                gap,
                width: '100%',
                overflow: 'hidden',
                ...style,
            }}
        >
            {enhancedChildren}
        </Box>
      </RectUpdateContext.Provider>
    );
};

export default FlexContainer